import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import accessLocalStorage, { ls } from 'services/localStorage';
import { SPARC_BASE, SHELL_ORIGIN } from 'utils/config';
import { TREEZ_AUTH_BASE } from 'services/api/api';
import routeConfig from 'routes/config';
import { isTokenLapsed, isAccessTokenPresent } from 'services/auth';
import { isDashboardInNativeMode } from './dashboard';
import { v4 as uuidv4 } from 'uuid';
import { ORG_LEVEL_PARAM, SELECTED_ENTITY_IDS_PARAM } from './constants';
import redirectToLogin from './redirectToLogin';
import { REFRESH_TOKENS_MESSAGE } from './shell-messages';

interface IAxiosResolve {
  (
    value: InternalAxiosRequestConfig | PromiseLike<InternalAxiosRequestConfig>
  ): void;
}

interface IAxiosReject {
  (reason?: string | Error): void;
}

/**
 * Creates an Axios instance with base URL configured.
 * @returns An AxiosInstance
 */
export const createApiInstance = (): AxiosInstance => {
  const baseURL = SPARC_BASE;
  const config = { baseURL };
  return axios.create(config);
};

/**
 * Checks if the request has org ID header. Aborts request if absent.
 * @param {AxiosRequestConfig} config - request config object
 * @returns a promise that resolves to an AxiosRequestConfig object.
 */
export const verifyOrgHeader = async (config: InternalAxiosRequestConfig) => {
  return new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
    if (!config.headers['X-Organization-Id']) {
      // skipcq: JS-0002
      console.log({
        message: 'Org ID header not found',
        url: config.url,
      });
      return reject(new Error('Organization ID not found'));
    }

    return resolve(config);
  });
};
export const verifyEntityHeader = async (
  config: InternalAxiosRequestConfig
) => {
  return new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
    if (!config.headers['entity-id']) {
      // skipcq: JS-0002
      console.log({
        message: 'Entity ID header not found',
        url: config.url,
      });
      return reject(new Error('Entity ID not found'));
    }

    return resolve(config);
  });
};

export type ISSOTokens = {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  idToken?: string;
};

export const requestSSOTokenRefresh = async (
  accessToken: string,
  refreshToken: string
): Promise<ISSOTokens | null> => {
  const apiConfig = {
    headers: {
      'x-application': 'MSO',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    baseURL: TREEZ_AUTH_BASE,
  };
  const body = new URLSearchParams({ accessToken, refreshToken });

  const response = await axios.post('/tokens/refresh', body, apiConfig);
  const tokens = response?.data;

  if (!tokens?.accessToken || !tokens?.refreshToken) return null;

  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresIn: tokens?.expiresIn,
    idToken: tokens?.idToken,
  };
};

export const describeError = (e: unknown): string => {
  if (axios.isAxiosError(e)) {
    const status = e.response?.status;
    return status ? `${e.message} (HTTP ${status})` : e.message;
  }
  return e instanceof Error ? e.message : String(e);
};

const isTokenRejection = (e: unknown): boolean => {
  if (!axios.isAxiosError(e)) return false;
  const status = e.response?.status;
  if (!status) return false;
  return status === 400 || status === 401 || status === 403;
};

let refreshInFlight: Promise<ISSOTokens | null> | null = null;

export const refreshSession = (
  accessToken: string,
  refreshToken: string
): Promise<ISSOTokens | null> => {
  if (!refreshInFlight) {
    refreshInFlight = requestSSOTokenRefresh(accessToken, refreshToken)
      .then((tokens) => {
        if (tokens) accessLocalStorage.updateUser(tokens);
        return tokens;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
};

export const canSpendRefreshToken = (): boolean => !isDashboardInNativeMode();

const toRefreshSSOToken = async (
  accessToken: string,
  refreshToken: string,
  config: InternalAxiosRequestConfig,
  resolve: IAxiosResolve
) => {
  if (!canSpendRefreshToken()) {
    window.parent.postMessage(
      { type: REFRESH_TOKENS_MESSAGE },
      SHELL_ORIGIN || '*'
    );
    config.headers.Authorization = `Bearer ${accessToken}`;
    return resolve(config);
  }

  try {
    const tokens = await refreshSession(accessToken, refreshToken);

    if (tokens) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return resolve(config);
    }
    console.error('Token refresh returned no usable tokens');
  } catch (e) {
    if (!isTokenRejection(e)) {
      console.error(
        'Token refresh unreachable, keeping session:',
        describeError(e)
      );
      config.headers.Authorization = `Bearer ${accessToken}`;
      return resolve(config);
    }
    console.error('Token refresh rejected:', describeError(e));
  }

  accessLocalStorage.clearItem(ls.USER);

  resolve(config);

  if (isDashboardInNativeMode()) {
    window.parent.postMessage(
      { type: REFRESH_TOKENS_MESSAGE },
      SHELL_ORIGIN || '*'
    );
    return;
  }

  redirectToLogin();
};

/**
 * Request interceptor to set access token in API call header
 * @param {AxiosRequestConfig} config - request config object
 * @returns a promise that resolves to an AxiosRequestConfig object.
 */
export const authInterceptor = async (config: InternalAxiosRequestConfig) => {
  return new Promise<InternalAxiosRequestConfig>(async (resolve, reject) => {
    // Skip auth for preflight/OPTIONS requests
    if (config.method?.toLowerCase() === 'options') {
      return resolve(config);
    }

    const userObject = accessLocalStorage.getUser();

    if (isAccessTokenPresent(userObject)) {
      const { accessToken, refreshToken } = userObject;

      if (isTokenLapsed(accessToken) && refreshToken) {
        return toRefreshSSOToken(accessToken, refreshToken, config, resolve);
      }

      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return resolve(config);
  });
};

/**
 * Request interceptor to set a unique Idempotency ID for each API call.
 * This ensures safe retry behavior on the server side for idempotent operations.
 *
 * @param {AxiosRequestConfig} config - Axios request configuration object.
 * @returns {Promise<AxiosRequestConfig>} A promise that resolves with the modified config.
 */
export const idempotencyInterceptor = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  return new Promise<InternalAxiosRequestConfig>((resolve) => {
    // Only add IdempotencyId if it's not already present
    if (!config.headers?.IdempotencyId) {
      config.headers.set('IdempotencyId', uuidv4());
    }
    resolve(config);
  });
};

/**
 * Request interceptor to add is_org_level and selected_entity_ids parameters to API calls.
 * These parameters indicate whether the request is for organization-level or store-level data,
 * and which specific stores to filter when in org-level view.
 *
 * @param {AxiosRequestConfig} config - Axios request configuration object.
 * @returns {Promise<AxiosRequestConfig>} A promise that resolves with the modified config.
 */
export const orgLevelInterceptor = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  return new Promise<InternalAxiosRequestConfig>((resolve) => {
    try {
      const isOrgLevel = accessLocalStorage.getIsOrgLevel();
      const selectedEntityIds = accessLocalStorage.getSelectedEntityIds();

      if (config.params) {
        config.params[ORG_LEVEL_PARAM.KEY] = isOrgLevel;
      } else {
        config.params = { [ORG_LEVEL_PARAM.KEY]: isOrgLevel };
      }

      if (isOrgLevel && selectedEntityIds.length > 0) {
        config.params[SELECTED_ENTITY_IDS_PARAM.KEY] = selectedEntityIds.join(',');
      }
    } catch (error) {
      console.error('Error in orgLevelInterceptor:', error);
      if (!config.params) {
        config.params = {};
      }
      config.params[ORG_LEVEL_PARAM.KEY] = false;
    }

    resolve(config);
  });
};
